import React, { useEffect, useMemo, useState } from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaSearch,
  FaSlidersH,
} from "react-icons/fa";

import {
  filterSettingsTabs,
  groupTabsByCategory,
} from "../utils/settingsDashboardUtils";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function findActiveGroup(groupedTabs, activeTab) {
  for (const [group, items] of Object.entries(groupedTabs || {})) {
    if ((items || []).some((item) => item.key === activeTab)) {
      return group;
    }
  }

  return Object.keys(groupedTabs || {})[0] || "";
}

export default function SettingsTabsShell({ tabs, activeTab, setActiveTab }) {
  const [tabSearch, setTabSearch] = useState("");
  const [openGroups, setOpenGroups] = useState([]);

  const filteredTabs = useMemo(
    () => filterSettingsTabs(tabs, tabSearch),
    [tabs, tabSearch],
  );

  const groupedTabs = useMemo(
    () => groupTabsByCategory(filteredTabs),
    [filteredTabs],
  );

  const groupedAllTabs = useMemo(() => groupTabsByCategory(tabs), [tabs]);

  const activeItem = tabs.find((tab) => tab.key === activeTab) || tabs[0];
  const ActiveComponent = activeItem?.Component;
  const ActiveIcon = activeItem?.Icon;

  const activeGroup = useMemo(
    () => findActiveGroup(groupedAllTabs, activeTab),
    [groupedAllTabs, activeTab],
  );

  const searchActive = tabSearch.trim().length > 0;
  const totalTabs = tabs.length;
  const filteredCount = filteredTabs.length;

  useEffect(() => {
    if (!activeGroup) return;

    setOpenGroups((prev) => {
      if (prev.includes(activeGroup)) return prev;
      return [activeGroup];
    });
  }, [activeGroup]);

  const displayedOpenGroups = useMemo(() => {
    if (searchActive) return Object.keys(groupedTabs);
    return openGroups;
  }, [searchActive, groupedTabs, openGroups]);

  const toggleGroup = (group) => {
    setOpenGroups((prev) => {
      if (prev.includes(group)) {
        return prev.filter((item) => item !== group);
      }

      return [group];
    });
  };

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <header className="border-b border-slate-200 bg-slate-50/80 p-4 dark:border-slate-800 dark:bg-slate-950/40 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Sections
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Paramètres
            </h2>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500 dark:text-slate-400">
              Les rubriques sont regroupées par panneaux pour garder une page
              compacte et facile à parcourir.
            </p>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <span className="rounded-full bg-white px-3 py-1.5 text-xs font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-800">
              {totalTabs} rubrique{totalTabs > 1 ? "s" : ""}
            </span>

            {searchActive ? (
              <span className="rounded-full bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 ring-1 ring-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20">
                {filteredCount} résultat{filteredCount > 1 ? "s" : ""}
              </span>
            ) : null}
          </div>
        </div>

        <div className="mt-5 grid gap-3 lg:grid-cols-[minmax(0,1fr),320px]">
          <div className="relative">
            <input
              type="text"
              value={tabSearch}
              onChange={(event) => setTabSearch(event.target.value)}
              placeholder="Rechercher un onglet..."
              className="h-12 w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#013E8E] focus:ring-4 focus:ring-blue-500/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:focus:border-blue-400 dark:focus:ring-blue-400/10"
            />

            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          </div>

          <select
            value={activeTab}
            onChange={(event) => setActiveTab(event.target.value)}
            className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:border-[#013E8E] focus:ring-4 focus:ring-blue-500/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:focus:border-blue-400 dark:focus:ring-blue-400/10"
          >
            {tabs.map((tab) => (
              <option key={tab.key} value={tab.key}>
                {tab.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-5 space-y-3">
          {Object.entries(groupedTabs).map(([group, items]) => {
            const isOpen = displayedOpenGroups.includes(group);
            const groupHasActiveTab = items.some((item) => item.key === activeTab);
            const activeTabInGroup = items.find((item) => item.key === activeTab);

            return (
              <div
                key={group}
                className={cn(
                  "overflow-hidden rounded-[22px] border bg-white transition dark:bg-slate-900",
                  groupHasActiveTab
                    ? "border-[#013E8E]/30 shadow-[0_18px_45px_-32px_rgba(1,62,142,0.85)] dark:border-indigo-400/40"
                    : "border-slate-200 dark:border-slate-800",
                )}
              >
                <button
                  type="button"
                  onClick={() => toggleGroup(group)}
                  className={cn(
                    "flex w-full items-center gap-3 px-4 py-3 text-left transition",
                    groupHasActiveTab
                      ? "bg-indigo-50/80 text-[#013E8E] dark:bg-indigo-500/10 dark:text-indigo-200"
                      : "bg-white text-slate-800 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800",
                  )}
                >
                  <span
                    className={cn(
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl",
                      groupHasActiveTab
                        ? "bg-[#013E8E] text-white dark:bg-indigo-500"
                        : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
                    )}
                  >
                    <FaSlidersH />
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold uppercase tracking-[0.12em]">
                      {group}
                    </span>

                    <span className="mt-0.5 block truncate text-xs font-medium text-slate-500 dark:text-slate-400">
                      {groupHasActiveTab && activeTabInGroup
                        ? `Actif : ${activeTabInGroup.label}`
                        : `${items.length} rubrique${items.length > 1 ? "s" : ""}`}
                    </span>
                  </span>

                  <span className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-700">
                    {items.length}
                  </span>

                  <span className="text-sm">
                    {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                  </span>
                </button>

                {isOpen ? (
                  <div className="border-t border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-950/30">
                    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                      {items.map(({ key, label, description, Icon }) => {
                        const active = activeTab === key;

                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => setActiveTab(key)}
                            title={description || label}
                            className={cn(
                              "group flex min-h-[58px] w-full items-center gap-3 rounded-2xl border px-3 py-2.5 text-left transition-all duration-200",
                              active
                                ? "border-transparent bg-gradient-to-r from-[#013E8E] via-indigo-600 to-blue-700 text-white shadow-[0_18px_35px_-24px_rgba(37,99,235,0.9)]"
                                : "border-slate-200 bg-white text-slate-700 hover:-translate-y-0.5 hover:bg-slate-50 hover:shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
                            )}
                          >
                            <span
                              className={cn(
                                "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-base transition",
                                active
                                  ? "bg-white/15 text-white ring-1 ring-white/20"
                                  : "bg-indigo-50 text-indigo-700 group-hover:bg-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300 dark:group-hover:bg-indigo-500/20",
                              )}
                            >
                              {Icon ? <Icon /> : null}
                            </span>

                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-bold">
                                {label}
                              </span>

                              {description ? (
                                <span
                                  className={cn(
                                    "mt-0.5 block truncate text-xs",
                                    active
                                      ? "text-white/75"
                                      : "text-slate-500 dark:text-slate-400",
                                  )}
                                >
                                  {description}
                                </span>
                              ) : null}
                            </span>

                            {active ? (
                              <span className="hidden rounded-full bg-white/15 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white ring-1 ring-white/20 lg:inline-flex">
                                Actif
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}

          {!filteredTabs.length ? (
            <div className="rounded-2xl bg-white p-4 text-sm text-slate-500 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:ring-slate-800">
              Aucun onglet trouvé.
            </div>
          ) : null}
        </div>
      </header>

      <main className="min-w-0 p-4 sm:p-5 lg:p-6">
        <div className="mb-5 rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                Onglet actif
              </p>

              <h3 className="mt-1 flex min-w-0 items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
                {ActiveIcon ? (
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                    <ActiveIcon />
                  </span>
                ) : null}

                <span className="truncate">{activeItem?.label}</span>
              </h3>

              {activeItem?.description ? (
                <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                  {activeItem.description}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <div id={`panel-${activeTab}`} role="tabpanel">
          {ActiveComponent ? <ActiveComponent /> : null}
        </div>
      </main>
    </section>
  );
}